/**
 * Chat interface layout with resizable split-pane design.
 * Left pane: message history, Right pane: collapsible control panel.
 * Responsive: side-by-side on desktop, Sheet overlay on mobile.
 */
import { PanelRightClose, PanelRightOpen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

import { Button } from "@/components/ui/button";
import {
	ResizablePanelGroup,
	ResizablePanel,
	ResizableHandle,
} from "@/components/ui/resizable";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetDescription,
} from "@/components/ui/sheet";
const MOBILE_BREAKPOINT = 1024;

interface ChatLayoutProps {
	messageArea: React.ReactNode;
	controlPanel: React.ReactNode;
}

/**
 * Hook to detect if viewport is mobile (<1024px)
 */
function useIsMobile() {
	const [isMobile, setIsMobile] = useState(
		typeof window !== "undefined" ? window.innerWidth < MOBILE_BREAKPOINT : false
	);

	useEffect(() => {
		/** Updates mobile state based on window width */
		const handleResize = () => {
			setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
		};

		window.addEventListener("resize", handleResize);
		return () => { window.removeEventListener("resize", handleResize); };
	}, []);

	return isMobile;
}

/**
 * Main chat layout component with resizable panels
 * @param props - Component props
 * @param props.messageArea - Content to display in the message area (left pane)
 * @param props.controlPanel - Content to display in the control panel (right pane)
 */
export function ChatLayout({ messageArea, controlPanel }: ChatLayoutProps) {
	const isMobile = useIsMobile();
	const controlPanelRef = useRef<ImperativePanelHandle>(null);
	const [isControlPanelCollapsed, setIsControlPanelCollapsed] = useState(false);
	const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false);

	// Keyboard shortcut: Cmd/Ctrl+Shift+S to toggle control panel
	useEffect(() => {
		/**
		 * Handles keyboard shortcut to toggle control panel
		 * @param e - Keyboard event
		 */
		const handleKeyDown = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "s") {
				e.preventDefault();
				if (isMobile) {
					setIsMobileSheetOpen((prev) => !prev);
				}
				else {
					// Toggle control panel using ref
					if (controlPanelRef.current?.isCollapsed()) {
						controlPanelRef.current.expand();
					}
					else {
						controlPanelRef.current?.collapse();
					}
				}
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => { window.removeEventListener("keydown", handleKeyDown); };
	}, [isMobile]);

	// Mobile layout: full-screen message area with Sheet for controls
	if (isMobile) {
		return (
			<div className="flex h-full flex-col">
				{/* Mobile header with toggle button */}
				<div className="flex items-center justify-between border-b p-2">
					<span className="text-sm font-medium">Agent Chat</span>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => { setIsMobileSheetOpen(true); }}
						aria-label="Open controls"
					>
						<PanelRightOpen className="h-4 w-4" />
					</Button>
				</div>

				{/* Message area */}
				<div className="flex-1 overflow-auto">{messageArea}</div>

				{/* Control panel as Sheet */}
				<Sheet open={isMobileSheetOpen} onOpenChange={setIsMobileSheetOpen}>
					<SheetContent side="right" className="w-full sm:max-w-md">
						<SheetHeader>
							<SheetTitle>Controls</SheetTitle>
							<SheetDescription>
								Configure model settings and manage files
							</SheetDescription>
						</SheetHeader>
						<div className="mt-4">{controlPanel}</div>
					</SheetContent>
				</Sheet>
			</div>
		);
	}

	// Desktop layout: resizable split pane
	return (
		<div className="flex h-full flex-col">
			{/* Desktop header with toggle button */}
			<div className="flex items-center justify-between border-b px-4 py-2">
				<span className="text-sm font-medium">Agent Chat</span>
				<Button
					variant="ghost"
					size="sm"
					onClick={() => {
						if (controlPanelRef.current?.isCollapsed()) {
							controlPanelRef.current.expand();
						}
						else {
							controlPanelRef.current?.collapse();
						}
					}}
					aria-label={isControlPanelCollapsed ? "Show controls" : "Hide controls"}
					title="Toggle controls (Ctrl+Shift+S)"
				>
					{isControlPanelCollapsed ? (
						<PanelRightOpen className="h-4 w-4" />
					) : (
						<PanelRightClose className="h-4 w-4" />
					)}
				</Button>
			</div>

			{/* Resizable panel content */}
			<ResizablePanelGroup
				direction="horizontal"
				autoSaveId="nucleus-chat-layout"
				className="flex-1"
			>
				{/* Message area: default 70%, min 40% */}
				<ResizablePanel defaultSize={70} minSize={40}>
					<div className="h-full overflow-auto">
						{messageArea}
					</div>
				</ResizablePanel>

				<ResizableHandle withHandle />

				{/* Control panel: default 30%, min 20%, collapsible */}
				<ResizablePanel
					ref={controlPanelRef}
					defaultSize={30}
					minSize={20}
					collapsible
					collapsedSize={0}
					onCollapse={() => { setIsControlPanelCollapsed(true); }}
					onExpand={() => { setIsControlPanelCollapsed(false); }}
				>
					<div className="h-full overflow-auto border-l bg-background p-4">
						{controlPanel}
					</div>
				</ResizablePanel>
			</ResizablePanelGroup>
		</div>
	);
}
